import { useState, useEffect, useRef } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
} from "recharts";
import { Pencil, Download } from "lucide-react";
import {
  getExpenseDeviation,
  addCategoryComment,
} from "../../utils/ActionCreator";

const CustomTooltip = ({ data, onEdit, position, chartContainerRef }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [comment, setComment] = useState("");
  const tooltipRef = useRef(null);

  const { categoryName, actual, comment: existingComment } = data;

  useEffect(() => {
    setComment(existingComment || "");
  }, [existingComment]);

  const getTooltipStyle = () => {
    const TOOLTIP_WIDTH = 220;
    const TOOLTIP_HEIGHT = isEditing ? 160 : 120;
    const containerWidth = chartContainerRef?.current?.offsetWidth || 600;

    let left = position.x - TOOLTIP_WIDTH / 2;
    let top = position.y - TOOLTIP_HEIGHT - 1;

    if (left < 8) left = 8;
    if (left + TOOLTIP_WIDTH > containerWidth - 8)
      left = containerWidth - TOOLTIP_WIDTH - 8;
    if (top < 8) top = position.y + 12;

    return { left, top };
  };

  const { left, top } = getTooltipStyle();

  return (
    <div
      ref={tooltipRef}
      style={{
        position: "absolute",
        left,
        top,
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        padding: "14px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        width: "220px",
        pointerEvents: "auto",
        zIndex: 100,
      }}
    >
      {!isEditing ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#94a3b8",
                  fontWeight: 600,
                  marginBottom: "2px",
                }}
              >
                {categoryName}
              </div>
              <div
                style={{ fontSize: "16px", fontWeight: 700, color: "#0f172a" }}
              >
                ₹ {actual?.toLocaleString()}
              </div>
            </div>
            <button
              onClick={() => setIsEditing(true)}
              style={{
                padding: "6px 8px",
                backgroundColor: "#015c57",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                color: "#fff",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Pencil size={14} />
            </button>
          </div>

          {existingComment && (
            <>
              <div style={{ borderTop: "1px solid #f1f5f9" }} />
              <div
                style={{
                  fontSize: "16px",
                  color: "#0c0d0d",
                  fontStyle: "italic",
                  // lineHeight: "1.5",
                  wordBreak: "break-word",
                }}
              >
                {existingComment}
              </div>
            </>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <div style={{ fontSize: "11px", color: "#015c57", fontWeight: 700 }}>
            {categoryName} — Add Note
          </div>
          <input
            type="text"
            placeholder="Add note..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            style={{
              width: "100%",
              padding: "7px 9px",
              border: "1.5px solid #015c57",
              borderRadius: "6px",
              fontSize: "12px",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          <div style={{ display: "flex", gap: "6px" }}>
            <button
              onClick={() => {
                onEdit(categoryName, comment);
                setIsEditing(false);
              }}
              style={{
                flex: 1,
                padding: "6px 10px",
                backgroundColor: "#015c57",
                border: "none",
                borderRadius: "6px",
                color: "#fff",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              Save
            </button>
            <button
              onClick={() => setIsEditing(false)}
              style={{
                flex: 1,
                padding: "6px 10px",
                backgroundColor: "#f1f5f9",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function ExpenseBarChart() {
  const [chartData, setChartData] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [tooltipHover, setTooltipHover] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const chartContainerRef = useRef(null);

  useEffect(() => {
    getExpenseDeviation(
      null,
      (data) => {
        const formatted = data.map((item) => ({
          categoryName: item.categoryName,
          // actual: item.actual,
          // comment: item.comment || "",
          actual: item.actual || 1,
          comment: item.comment || "",
        }));
        setChartData(formatted);
      },
      (error) => console.error(error),
    );
  }, []);

  const downloadCSV = () => {
    const headers = ["Category", "Actual Amount", "Comment"];
    const rows = chartData.map((item) => [
      item.categoryName,
      item.actual,
      item.comment || "",
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "expense-report.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEdit = (categoryName, comment) => {
    const payload = { categoryName, comment, commentBy: "Finance Team" };
    addCategoryComment(
      payload,
      () => {
        setChartData((prev) =>
          prev.map((d) =>
            d.categoryName === categoryName ? { ...d, comment } : d,
          ),
        );
      },
      (error) => console.error("Comment API Error:", error),
    );
  };

  const CustomBar = (props) => {
    const { x, y, width, height, index, fill } = props;
    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        rx={6}
        ry={6}
        style={{ cursor: "pointer" }}
        onMouseEnter={() => {
          const containerRect =
            chartContainerRef.current?.getBoundingClientRect();
          const svgRect = chartContainerRef.current
            ?.querySelector("svg")
            ?.getBoundingClientRect();

          if (svgRect && containerRect) {
            const barCenterX =
              svgRect.left - containerRect.left + x + width / 2;
            const barTopY = svgRect.top - containerRect.top + y;
            setTooltipPos({ x: barCenterX, y: barTopY });
          }
          setActiveIndex(index);
        }}
      />
    );
  };

  return (
    <div
      ref={chartContainerRef}
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        border: "1px solid #E5E7EB",
        padding: "24px",
        position: "relative",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "10px",
        }}
      >
        <button
          onClick={downloadCSV}
          style={{
            padding: "6px 15px",
            backgroundColor: "#015c57",
            color: "#fff",
            border: "none",
            borderRadius: "20px",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: 600,
          }}
        >
          Download
        </button>
      </div>

      <ResponsiveContainer width="100%" height={315}>
        <BarChart
          data={chartData}
          onMouseLeave={() => {
            if (!tooltipHover) setActiveIndex(null);
          }}
        >
          <CartesianGrid stroke="#E5E7EB" vertical={false} horizontal={false} />
          <defs>
            <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor=" rgb(3, 107, 101)" />
              <stop offset="100%" stopColor="rgb(5, 200, 188)" />
            </linearGradient>
          </defs>
          <XAxis dataKey="categoryName" axisLine={false} tickLine={false} />
          <YAxis
            axisLine={false}
            tickLine={false}
            scale="log"
            domain={[1, "auto"]}
            allowDataOverflow
            tickFormatter={(v) => {
              if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
              if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
              if (v >= 1000) return `₹${(v / 1000).toFixed(1)}k`;
              return `₹${v}`;
            }}
          />
          <Bar
            dataKey="actual"
            barSize={40}
            shape={(props) => (
              <CustomBar
                {...props}
                // fill={activeIndex === props.index ? "#013d39" : "#015c57"}
                fill="url(#actualGradient)"
              />
            )}
          />
        </BarChart>
      </ResponsiveContainer>

      {activeIndex !== null && chartData[activeIndex] && (
        <div
          onMouseEnter={() => setTooltipHover(true)}
          onMouseLeave={() => {
            setTooltipHover(false);
            setActiveIndex(null);
          }}
        >
          <CustomTooltip
            data={chartData[activeIndex]}
            onEdit={handleEdit}
            position={tooltipPos}
            chartContainerRef={chartContainerRef}
          />
        </div>
      )}
    </div>
  );
}
